import { useEffect, useState } from "react";
import { useIsMounted } from "./useIsMounted";
import { isNil, isNotNil } from "client/Misc/utils";

export function useExternalJsScript(
	scriptUrl: string
): boolean | undefined {
	const isMounted: () => boolean = useIsMounted();

	const [didLoadSuccessful, setDidLoadSuccessful] = useState<boolean | undefined>(undefined);

	useEffect(
		() => {
			const load = async () => {
				setDidLoadSuccessful(undefined);

				let wasSuccessful = false;
				try {
					await loadScriptAsynchronously(scriptUrl);
					wasSuccessful = true;

				} catch (e) {
					console.error("Failed to load the external JavaScript", e);
				}

				if (isMounted()) {
					setDidLoadSuccessful(wasSuccessful);
				}
			};

			load();
		},
		[scriptUrl, isMounted]
	);

	return didLoadSuccessful;
};

const _asyncScriptCache = new Map<string, Promise<HTMLScriptElement>>();

export function loadScriptAsynchronously(
	scriptUrl: string,
	dataAttributes?: Readonly<Record<string, string | null>>
): Promise<HTMLScriptElement> {
	const cachedScript: Promise<HTMLScriptElement> | undefined = _asyncScriptCache.get(scriptUrl);

	if (isNotNil(cachedScript)) {
		return cachedScript.then(
			(scriptElement: HTMLScriptElement): HTMLScriptElement => {
				applyDataAttributes(scriptElement, dataAttributes);

				return scriptElement;
			}
		);
	}

	const result = new Promise<HTMLScriptElement>(
		(resolve, reject) => {
			const scriptElement: HTMLScriptElement = document.createElement("script");
			scriptElement.src = scriptUrl;
			scriptElement.async = true;
			scriptElement.type = "text/javascript";

			applyDataAttributes(scriptElement, dataAttributes);

			scriptElement.onload = () => resolve(scriptElement);
			scriptElement.onerror = () => reject();

			document.head
				.appendChild(scriptElement);
		}
	);

	_asyncScriptCache.set(scriptUrl, result);

	return result;
}

function applyDataAttributes(
	scriptElement: HTMLScriptElement,
	dataAttributes?: Readonly<Record<string, string | null>>
): void {
	for (const [name, value] of Object.entries(dataAttributes ?? {})) {
		if (isNil(value)) {
			continue;
		}

		scriptElement.dataset[name] = value;
	}
}